import PropTypes from 'prop-types';
import React, { Fragment, useMemo } from 'react';
import get from 'lodash.get';
import classNames from 'classnames';
import { SKILL_NAMES, MEASURE_NAMES } from '../../../constants/assessments';
import { BM_PERIODS } from '../../../constants/enrollment';
import TableHeader from '../table-header/TableHeader';
import AssessLink from '../assess-link/AssessLink';
import { studentType } from '../../../constants/prop-types';
import {
    constructOLSAssessmentLink,
    constructOLSReviewLink,
} from '../../../common/navigation/navigation';
import { useAppContext } from '../../../common/contexts/AppContext';
import { localize, DICTIONARY_KEYS } from '../../../common/localization';
import { useLanguageContext } from '../../../common/contexts/LanguageContext';
import { useAppConfigContext } from '../../../common/contexts/AppConfigContext';
import { SPANISH } from '../../../constants/languages';
import { OLS_SPANISH, OLS, EDITIONS } from '../../../constants/applications';
import OLSMeasureScore from './OLSMeasureScore';
import useAssessIcons from '../../../common/hooks/useAssessIcons';
import getAvailableBenchmarkPeriods from '../../../common/utils/getAvailableBenchmarkPeriods';

const DISABLED_ICONS = {
    [AssessLink.MODE.OPTIONAL]: AssessLink.MODE.OPTIONAL_DISABLED,
    [AssessLink.MODE.REQUIRED]: AssessLink.MODE.DISABLED,
    [AssessLink.MODE.RESUME]: AssessLink.MODE.RESUME_DISABLED,
};

export const getOLSDetailsForPeriod = (
    student,
    period,
    classId,
    lang,
    results,
    edition,
    isBMAssessmentIconEnabled,
    isBMAssessmentIconVisible,
) => {
    const { id } = student;
    const params = {
        period,
        lang,
        classId,
    };

    const [result] = get(results, [period, id], []);

    if (result && result.isComplete) {
        return (
            <OLSMeasureScore
                score={result.score}
                interpretation={result.interpretation}
                studentId={student.id}
                period={period}
                classId={classId}
                lang={lang}
                resultId={result.clientId}
            />
        );
    }
    if (result && !result.isComplete) {
        const assessLinkMode = isBMAssessmentIconEnabled(period)
            ? AssessLink.MODE.RESUME
            : AssessLink.MODE.RESUME_DISABLED;
        return (
            <AssessLink
                mode={assessLinkMode}
                path={constructOLSReviewLink(id, { ...params, resultId: result.clientId })}
            />
        );
    }
    if (isBMAssessmentIconVisible(period)) {
        const assessLink =
            edition === EDITIONS.MCLASS_TX_EDITION
                ? AssessLink.MODE.OPTIONAL
                : AssessLink.MODE.REQUIRED;

        const assessLinkMode = isBMAssessmentIconEnabled(period)
            ? assessLink
            : DISABLED_ICONS[assessLink];

        return <AssessLink mode={assessLinkMode} path={constructOLSAssessmentLink(id, params)} />;
    }

    return <div />;
};

const OLSSummary = ({ student, classId, results }) => {
    const { selectedAppGroup, fourthPeriodAvailability } = useAppContext();
    const lang = useLanguageContext();
    const appName = lang === SPANISH ? OLS_SPANISH : OLS;
    const { appConfigs, edition } = useAppConfigContext();
    const benchmarkPeriods = useMemo(
        () => getAvailableBenchmarkPeriods(selectedAppGroup, fourthPeriodAvailability),
        [fourthPeriodAvailability, selectedAppGroup],
    );
    const hasFourthPeriod = benchmarkPeriods.length > 3;
    const headerColumns = useMemo(
        () => [
            edition === EDITIONS.MCLASS_TX_EDITION
                ? SKILL_NAMES.LISTENING_COMPREHENSION
                : DICTIONARY_KEYS.AMPLIFY_ORAL_LANGUAGE_SCREENER,
            ...benchmarkPeriods,
        ],
        [edition, benchmarkPeriods],
    );
    const { isBMAssessmentIconEnabled, isBMAssessmentIconVisible } = useAssessIcons(
        benchmarkPeriods,
    );

    const licensedSkill = get(appConfigs, [appName, 'licensedMeasures'], []).find(
        licensedItem => licensedItem.skillName === SKILL_NAMES.ORAL_LANGUAGE,
    );

    if (!get(licensedSkill, 'grades').includes(student.grade)) {
        return null;
    }

    return (
        <Fragment>
            <TableHeader columns={headerColumns} hasFourthPeriod={hasFourthPeriod} />
            <div className='dashboard-measure-scores' data-test-id="ols-measure-scores">
                <div
                    className={classNames('dashboard-measure-info', {
                        'dashboard-has-fourth-period': hasFourthPeriod,
                    })}
                    key={`ol${lang}`}
                >
                    <div className='dashboard-skill'>
                        <span className='dashboard-skill-name'>{localize(SKILL_NAMES.ORAL_LANGUAGE)}</span>
                        <span className='dashboard-measure-name'>{localize(MEASURE_NAMES.OL)}</span>
                    </div>

                    {BM_PERIODS.map(period => (
                        <Fragment key={period}>
                            {getOLSDetailsForPeriod(
                                student,
                                period,
                                classId,
                                lang,
                                results,
                                edition,
                                isBMAssessmentIconEnabled,
                                isBMAssessmentIconVisible,
                                selectedAppGroup,
                            )}
                        </Fragment>
                    ))}
                </div>
            </div>

            <div className='dashboard-empty-divider' />
        </Fragment>
    );
};

OLSSummary.propTypes = {
    student: studentType.isRequired,
    classId: PropTypes.string.isRequired,
    results: PropTypes.object,
};

OLSSummary.defaultProps = {
    results: {},
};

export default OLSSummary;
